import { t } from "@lingui/macro";
import { functions, getReleasedCallRecordingUrl } from "@repo/ui";
import * as Sentry from "@sentry/react";
import { httpsCallable } from "firebase/functions";
import {
	AlertTriangle,
	CheckCircle2,
	CircleDot,
	ClipboardCheck,
	Clock,
	Inbox,
	Phone,
	ShieldCheck,
	Timer,
} from "lucide-react";
import React, { useEffect, useMemo } from "react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import { useCallListActions } from "../hooks/useCallListActions";
import { i18n } from "../i18n";
import type { Agent } from "../types";
import { isAdminRole } from "../utils/auth";
import { isCallUnread } from "../utils/callReadStatus";
import { formatDuration, summarizeCallStats } from "../utils/dashboardStats";
import { CallActivityGraph } from "./CallActivityGraph";
import { CallRecord } from "./CallRecord";
import { ContentState } from "./ContentState";
import { PageHeader } from "./PageHeader";
import { TagCounters } from "./TagCounters";
import { Button } from "./ui/button";
import {
	Card,
	CardAction,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "./ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

interface DashboardProps {
	onNavigateToCall: (callId?: string) => void;
	onNavigateToAgent: (agentName?: string) => void;
}

function hasDeploymentError(agent: Agent): boolean {
	return [agent.stagingDeployment?.status, agent.liveDeployment?.status].some(
		(status) =>
			typeof status === "string" && status.toLowerCase().includes("error"),
	);
}

export function Dashboard({
	onNavigateToCall,
	onNavigateToAgent,
}: DashboardProps) {
	const {
		dashboardRecentCalls,
		actionItems,
		agents,
		calls,
		callsLoading,
		callsError,
		user,
		companyProfile,
		language,
		dailyCallStats,
		serviceTags,
	} = useData();

	const { markRead, toggleArchive } = useCallListActions(user?.companyId);
	const isAdmin = isAdminRole(user?.role);
	const [audioUrl, setAudioUrl] = React.useState<string | null>(null);
	const audioRef = React.useRef<HTMLAudioElement>(null);
	const [playbackRate, setPlaybackRate] = React.useState(1);
	const [pendingApprovals, setPendingApprovals] = React.useState({
		deployments: 0,
		operations: 0,
	});
	const canReviewApprovals =
		user?.role === "admin" &&
		companyProfile?.features?.apiAccess === true &&
		companyProfile.id === user?.companyId;
	useEffect(() => {
		if (!canReviewApprovals || !companyProfile?.id) {
			setPendingApprovals({ deployments: 0, operations: 0 });
			return;
		}
		let active = true;
		const companyId = companyProfile.id;
		const load = async () => {
			const [deployments, operations] = await Promise.allSettled([
				httpsCallable<{ companyId: string }, { requests: unknown[] }>(
					functions,
					"listExternalAgentDeploymentApprovals",
				)({ companyId }),
				httpsCallable<{ companyId: string }, { requests: unknown[] }>(
					functions,
					"listExternalApprovalRequests",
				)({ companyId }),
			]);
			if (active)
				setPendingApprovals({
					deployments:
						deployments.status === "fulfilled"
							? deployments.value.data.requests.length
							: 0,
					operations:
						operations.status === "fulfilled"
							? operations.value.data.requests.length
							: 0,
				});
		};
		void load();
		return () => {
			active = false;
		};
	}, [canReviewApprovals, companyProfile?.id]);

	const unreadCalls = useMemo(
		() => calls.filter((call) => isCallUnread(call.interactionStatus)),
		[calls],
	);
	const openActionItems = useMemo(
		() => actionItems.filter((item) => item.status !== "closed"),
		[actionItems],
	);
	const agentsWithDeploymentErrors = useMemo(
		() => agents.filter(hasDeploymentError),
		[agents],
	);
	const stats = summarizeCallStats(dailyCallStats);

	const attentionItems = [
		...(canReviewApprovals && pendingApprovals.deployments > 0
			? [
					{
						key: "live-approvals",
						icon: ShieldCheck,
						title: i18n._({
							id: "dashboard.attention.liveApprovals",
							message: "{count} Live deployment approvals pending",
							values: { count: pendingApprovals.deployments },
						}),
						description: t({
							id: "dashboard.attention.liveApprovalsDescription",
							message:
								"Review requested Live deployments in Developer settings.",
						}),
						href: "?page=settings&section=developer",
					},
				]
			: []),
		...(canReviewApprovals && pendingApprovals.operations > 0
			? [
					{
						key: "api-approvals",
						icon: ShieldCheck,
						title: i18n._({
							id: "dashboard.attention.apiApprovals",
							message: "{count} API operation approvals pending",
							values: { count: pendingApprovals.operations },
						}),
						description: t({
							id: "dashboard.attention.apiApprovalsDescription",
							message: "Review requested API operations in Developer settings.",
						}),
						href: "?page=settings&section=developer",
					},
				]
			: []),
		...(unreadCalls.length > 0
			? [
					{
						key: "unread",
						icon: Inbox,
						title: i18n._({
							id: "dashboard.attention.unreadCalls",
							message: "{count} calls need review",
							values: { count: unreadCalls.length },
						}),
						description: t({
							id: "dashboard.attention.unreadCallsDescription",
							message:
								"Open the calls queue and review the latest interactions.",
						}),
						onClick: () => onNavigateToCall(unreadCalls[0]?.id),
					},
				]
			: []),
		...(openActionItems.length > 0
			? [
					{
						key: "follow-up",
						icon: ClipboardCheck,
						title: i18n._({
							id: "dashboard.attention.openFollowUps",
							message: "{count} follow-ups are still open",
							values: { count: openActionItems.length },
						}),
						description: t({
							id: "dashboard.attention.openFollowUpsDescription",
							message:
								"Continue from the call where the follow-up was created.",
						}),
						onClick: () => onNavigateToCall(openActionItems[0]?.callSid),
					},
				]
			: []),
		...(agentsWithDeploymentErrors.length > 0
			? [
					{
						key: "deployment",
						icon: AlertTriangle,
						title: i18n._({
							id: "dashboard.attention.deploymentErrors",
							message: "{count} agents need deployment attention",
							values: { count: agentsWithDeploymentErrors.length },
						}),
						description: t({
							id: "dashboard.attention.deploymentErrorsDescription",
							message:
								"Review the failed Testing or Live deployment before retrying.",
						}),
						onClick: () =>
							onNavigateToAgent(agentsWithDeploymentErrors[0]?.name),
					},
				]
			: []),
	];

	const metrics = [
		{
			key: "calls",
			label: t({
				id: "dashboard.metrics.calls",
				message: "Calls last 30 days",
			}),
			value: stats.callCount.toLocaleString(language),
			icon: Phone,
			color: "bg-accent text-accent-ink",
		},
		{
			key: "average",
			label: t({
				id: "dashboard.metrics.averageDuration",
				message: "Average duration",
			}),
			value: formatDuration(
				stats.callCount ? stats.durationSeconds / stats.callCount : 0,
			),
			icon: Timer,
			color: "bg-secondary text-muted-foreground",
		},
		{
			key: "duration",
			label: t({
				id: "dashboard.metrics.totalDuration",
				message: "Total duration",
			}),
			value: formatDuration(stats.durationSeconds),
			icon: Clock,
			color: "bg-live-tint text-live",
		},
	];

	const handlePlayAudio = async (call: (typeof calls)[number]) => {
		if (!call.gcsUri) {
			toast.error(
				t({
					id: "recordingUnavailable",
					message: "No recording is available for this call.",
				}),
			);
			return;
		}

		setPlaybackRate(1);
		try {
			let url = call.gcsUri;
			if (url.startsWith("gs://")) {
				url = (
					await getReleasedCallRecordingUrl({
						companyId: user?.companyId ?? "",
						callSid: call.id,
					})
				).data.url;
			}
			setAudioUrl(url);
		} catch (error) {
			console.error(`Error getting audio URL for ${call.gcsUri}:`, error);
			Sentry.captureException(error);
			toast.error(
				t({
					id: "recordingLoadFailed",
					message: "The recording could not be loaded. Please try again.",
				}),
			);
		}
	};

	const getAgentCompany = (agentName: string): string | undefined =>
		agents.find((agent) => agent.name === agentName)?.companyId;

	return (
		<div className="mx-auto max-w-7xl space-y-6 px-4 sm:px-6 lg:px-8">
			<PageHeader
				title={t({ id: "dashboard", message: "Dashboard" })}
				description={t({
					id: "dashboardSubtitle",
					message: "Overview of your agents and recent activity",
				})}
			/>

			<section
				aria-labelledby="dashboard-attention-title"
				className="rounded-lg border-[1.5px] border-card-border bg-card shadow-card p-4 sm:p-5"
			>
				<div className="flex items-start gap-3">
					<div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-secondary text-muted-foreground">
						{attentionItems.length > 0 ? (
							<CircleDot className="h-5 w-5" />
						) : (
							<CheckCircle2 className="h-5 w-5 text-live" />
						)}
					</div>
					<div className="min-w-0 flex-1">
						<h2
							id="dashboard-attention-title"
							className="font-bold text-foreground"
						>
							{attentionItems.length > 0
								? t({
										id: "dashboard.attention.title",
										message: "Needs attention",
									})
								: t({
										id: "dashboard.attention.clearTitle",
										message: "You’re all caught up",
									})}
						</h2>
						<p className="mt-0.5 text-sm leading-6 text-muted-foreground">
							{attentionItems.length > 0
								? t({
										id: "dashboard.attention.description",
										message:
											"Review the items below to keep customer work moving.",
									})
								: t({
										id: "dashboard.attention.clearDescription",
										message:
											"There are no unread calls, open follow-ups, or deployment errors right now.",
									})}
						</p>
					</div>
				</div>
				{attentionItems.length > 0 ? (
					<div className="mt-4 grid grid-cols-[repeat(auto-fit,minmax(min(100%,240px),1fr))] gap-2 border-t border-card-border pt-4">
						{attentionItems.map((item) => {
							const Icon = item.icon;
							const content = (
								<>
									<Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
									<span className="min-w-0">
										<span className="block text-sm font-semibold text-foreground">
											{item.title}
										</span>
										<span className="mt-0.5 block text-xs leading-5 text-muted-foreground">
											{item.description}
										</span>
									</span>
								</>
							);
							const className =
								"flex min-h-20 items-start gap-3 rounded-lg bg-secondary p-3 text-start transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/30";
							return "href" in item ? (
								<a key={item.key} href={item.href} className={className}>
									{content}
								</a>
							) : (
								<button
									key={item.key}
									type="button"
									onClick={item.onClick}
									className={className}
								>
									{content}
								</button>
							);
						})}
					</div>
				) : null}
			</section>

			<section
				aria-label={t({
					id: "dashboard.operationalStats",
					message: "Call statistics — last 30 days",
				})}
				className="overflow-hidden rounded-lg border-[1.5px] border-card-border bg-card shadow-card"
			>
				{callsError ? (
					<p className="p-4 text-sm text-muted-foreground">
						{t({ id: "callsLoadFailed", message: "Calls could not be loaded" })}
					</p>
				) : (
					<dl
						className="flex flex-wrap gap-px bg-card-border"
						aria-busy={callsLoading}
					>
						{metrics.map((metric) => {
							const Icon = metric.icon;
							return (
								<div
									key={metric.key}
									className="flex min-w-0 flex-[1_1_max-content] items-center justify-center gap-2 bg-card px-4 py-3"
								>
									<div
										className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${metric.color}`}
									>
										<Icon className="h-4 w-4" />
									</div>
									<div className="flex min-w-0 items-baseline gap-2">
										<dt className="whitespace-nowrap text-sm text-muted-foreground">
											{metric.label}
										</dt>
										<dd
											className="order-first text-lg font-bold tabular-nums"
											dir="ltr"
										>
											{callsLoading ? "—" : metric.value}
										</dd>
									</div>
								</div>
							);
						})}
					</dl>
				)}
			</section>

			<div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
				<CallActivityGraph dailyStats={dailyCallStats} language={language} />
				<TagCounters dailyStats={dailyCallStats} serviceTags={serviceTags} />
			</div>

			<div>
				<Card>
					<CardHeader className="border-b border-card-border pb-4">
						<CardTitle className="flex items-center gap-2">
							<Phone className="h-5 w-5" />
							<span>{t({ id: "recentCalls", message: "Recent Calls" })}</span>
						</CardTitle>
						<CardDescription>
							{t({
								id: "latestInteractions",
								message: "Latest agent interactions",
							})}
						</CardDescription>
						<CardAction>
							<Button
								variant="link"
								className="h-auto px-0 py-0 text-accent-ink"
								onClick={() => onNavigateToCall()}
							>
								{t({ id: "viewAllCalls", message: "View All Calls" })}
							</Button>
						</CardAction>
					</CardHeader>
					<CardContent>
						{callsLoading ? (
							<div className="space-y-3" aria-busy="true">
								{Array.from({ length: 3 }, (_, index) => (
									<div
										key={index}
										className="h-24 animate-pulse rounded-lg bg-muted"
									/>
								))}
							</div>
						) : callsError ? (
							<ContentState
								icon={<AlertTriangle className="h-5 w-5" />}
								title={t({
									id: "callsLoadFailed",
									message: "Calls could not be loaded",
								})}
								description={t({
									id: "callsLoadFailedDescription",
									message:
										"Check your connection and refresh the page to try again.",
								})}
							/>
						) : dashboardRecentCalls.length === 0 ? (
							<ContentState
								icon={<Phone className="h-5 w-5" />}
								title={t({
									id: "dashboard.noCallsTitle",
									message: "No calls yet",
								})}
								description={t({
									id: "dashboard.noCallsDescription",
									message:
										"Recent interactions will appear here after an agent handles a call.",
								})}
							/>
						) : (
							<>
								<div className="space-y-3">
									{dashboardRecentCalls.map((call) => (
										<CallRecord
											key={call.id}
											call={call}
											onClick={() => onNavigateToCall(call.id)}
											onPlay={(event) => {
												event.stopPropagation();
												handlePlayAudio(call);
											}}
											onMarkRead={(e) => {
												e.stopPropagation();
												void markRead(call.id, true);
											}}
											onArchive={(e) => {
												e.stopPropagation();
												void toggleArchive(call.id, call.interactionStatus);
											}}
											onMarkUnread={(event) => {
												event.stopPropagation();
												markRead(call.id, false);
											}}
											language={language}
											isAdmin={isAdmin}
											getAgentCompany={getAgentCompany}
										/>
									))}
								</div>
							</>
						)}
					</CardContent>
				</Card>
			</div>

			<Dialog
				open={!!audioUrl}
				onOpenChange={(open) => !open && setAudioUrl(null)}
			>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>
							{t({ id: "playRecording", message: "Play Recording" })}
						</DialogTitle>
					</DialogHeader>
					<div className="space-y-4">
						<div className="flex items-center justify-center rounded-lg bg-secondary p-6">
							{audioUrl ? (
								<audio
									ref={audioRef}
									controls
									autoPlay
									className="w-full"
									src={audioUrl}
									onRateChange={(event) =>
										setPlaybackRate(event.currentTarget.playbackRate)
									}
								/>
							) : null}
						</div>
						<div className="flex justify-center gap-2">
							{[1, 1.5, 2].map((rate) => (
								<Button
									key={rate}
									variant={playbackRate === rate ? "default" : "outline"}
									size="sm"
									onClick={() => {
										if (audioRef.current) {
											audioRef.current.playbackRate = rate;
										}
									}}
								>
									{rate}x
								</Button>
							))}
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
}
